import { useState, FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { postSendResetPasswordCode, ApiRequestError } from '@/api';
import { FormSummaryMessage, InlineFieldError } from '@/components/ui/FormFeedback';

const STORAGE_EMAIL_KEY = 'gf_pwd_reset_email';
const REMEMBER_KEY = 'gf_remembered_email';

export default function LoginForgotPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state ?? {}) as { email?: string; from?: string };

  const [email, setEmail] = useState(() => {
    const fromState = locationState.email?.trim();
    if (fromState) return fromState;
    return localStorage.getItem(REMEMBER_KEY)?.trim() || '';
  });
  const [fieldError, setFieldError] = useState('');
  const [formError, setFormError] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError('');
    const trimmed = email.trim();
    if (!trimmed) {
      setFieldError('Please enter your email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setFieldError('Please enter a valid email address.');
      return;
    }

    setFieldError('');
    setSending(true);
    try {
      await postSendResetPasswordCode(trimmed);
      sessionStorage.setItem(STORAGE_EMAIL_KEY, trimmed);
      navigate('/login/password/verify', {
        state: locationState.from ? { from: locationState.from } : undefined,
      });
    } catch (err) {
      setFormError(err instanceof ApiRequestError ? err.message : 'Failed to send verification code.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6 relative">
      <button
        type="button"
        onClick={() => navigate('/login', { state: locationState.from ? { from: locationState.from } : undefined })}
        className="absolute top-6 left-6 flex items-center gap-1.5 text-gray-400 hover:text-gray-700 transition-colors cursor-pointer group"
      >
        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-gray-200 group-hover:border-gray-300 transition-colors">
          <i className="ri-arrow-left-line text-sm"></i>
        </div>
        <span className="text-xs font-medium">Back to Sign In</span>
      </button>

      <div className="flex flex-col items-center mb-10">
        <div className="w-14 h-14 rounded-lg bg-green-600 flex items-center justify-center mb-4">
          <i className="ri-lock-password-line text-white text-2xl"></i>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Change Password</h1>
        <p className="text-sm text-gray-500 mt-1 text-center max-w-xs">
          Enter your account email and we will send a verification code.
        </p>
      </div>

      <div className="w-full max-w-sm bg-white rounded-lg border border-gray-100 p-3">
        <form lang="en" noValidate onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label htmlFor="reset-email" className="block text-xs font-normal text-gray-600 mb-1.5">
              Email
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-mail-line text-base"></i>
              </div>
              <input
                id="reset-email"
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(ev) => {
                  setEmail(ev.target.value);
                  setFieldError('');
                }}
                placeholder="you@example.com"
                aria-invalid={!!fieldError}
                aria-describedby={fieldError ? 'reset-email-error' : undefined}
                className={`w-full pl-9 pr-4 py-2.5 text-sm font-normal border rounded-lg focus:outline-none focus:ring-1 transition placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
                  fieldError
                    ? 'border-red-300 bg-red-50/35 focus:border-red-400 focus:ring-red-100'
                    : 'border-gray-200 bg-gray-50 focus:border-green-500 focus:ring-green-200'
                }`}
              />
            </div>
            <InlineFieldError id="reset-email-error" message={fieldError} />
          </div>

          <FormSummaryMessage message={formError} />

          <button
            type="submit"
            disabled={sending}
            className="w-full h-11 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white text-sm font-normal rounded-lg transition disabled:opacity-60 cursor-pointer whitespace-nowrap flex items-center justify-center"
          >
            {sending ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin text-base"></i>
                Sending code…
              </span>
            ) : (
              'Send verification code'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
